import { useState } from 'react';
import { Badge, Button, Card, Coin, SectionHeading } from '@/components/ui';
import { BookingTypeIcon } from '@/components/companion/ServiceIcon';
import { bookingTypeLabel } from '@/lib/data/services';
import {
  useBookings,
  acceptBooking,
  cancelBooking,
  completeBooking,
  formatWhen,
  formatDuration,
  isLive,
  isExpiredRequest,
  STATUS_LABEL,
  STATUS_TONE,
  type Booking,
} from '@/lib/data/bookings';

/**
 * Every booking, from both sides. project.md §22 and §30.
 *
 * One page rather than separate "my bookings" and "my requests" screens.
 * A companion is also a user — they book people too — and forcing them to
 * remember which tab their evening plans are on is friction for nothing.
 * The role badge on each card says which side they are on.
 */
export default function Bookings() {
  const { bookings, loading, error, refresh } = useBookings();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const live = bookings.filter((b) => isLive(b.status));
  const past = bookings.filter((b) => !isLive(b.status));

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusyId(id);
    setActionError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'That did not work.');
    }
    setBusyId(null);
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-32 animate-pulse rounded-2xl"
            style={{ background: 'var(--surface-2)' }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12">
      <header className="animate-rise">
        <p className="eyebrow">Your time</p>
        <h1 className="mt-2 text-3xl font-semibold">Bookings</h1>
      </header>

      {(error || actionError) && (
        <div
          className="rounded-xl border-l-[3px] px-4 py-3 text-sm"
          style={{ borderColor: 'var(--danger)', background: 'var(--danger-soft)' }}
        >
          {actionError ?? error}
        </div>
      )}

      <section>
        <SectionHeading eyebrow={`${live.length} open`}>Coming up</SectionHeading>

        {live.length === 0 ? (
          <Card className="px-6 py-14 text-center">
            <p className="font-medium">Nothing booked</p>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-[var(--ink-2)]">
              Find someone to spend time with, and it will show up here.
            </p>
            <div className="mt-5">
              <Button to="/services">Browse services</Button>
            </div>
          </Card>
        ) : (
          <ul className="flex flex-col gap-4">
            {live.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                busy={busyId === b.id}
                onAccept={() => act(b.id, () => acceptBooking(b.id))}
                onCancel={(reason) => act(b.id, () => cancelBooking(b.id, reason))}
                onComplete={() => act(b.id, () => completeBooking(b.id))}
                onNoShow={() => act(b.id, () => cancelBooking(b.id, 'Did not turn up', true))}
              />
            ))}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <SectionHeading eyebrow={`${past.length}`}>Past</SectionHeading>
          <ul className="flex flex-col gap-3">
            {past.map((b) => (
              <PastRow key={b.id} booking={b} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function BookingCard({
  booking: b,
  busy,
  onAccept,
  onCancel,
  onComplete,
  onNoShow,
}: {
  booking: Booking;
  busy: boolean;
  onAccept: () => void;
  onCancel: (reason?: string) => void;
  onComplete: () => void;
  onNoShow: () => void;
}) {
  const [confirming, setConfirming] = useState<'cancel' | null>(null);
  const mine = b.role === 'companion';

  return (
    <Card as="li" className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span
            className="grid size-10 shrink-0 place-items-center rounded-xl"
            style={{ background: 'var(--surface-2)', color: 'var(--marigold)' }}
          >
            <BookingTypeIcon type={b.booking_type} size={20} />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold">{b.category_name}</h3>
            <p className="mt-0.5 text-sm text-[var(--ink-2)]">
              {bookingTypeLabel(b.booking_type)}
              {b.scheduled_start && (
                <>
                  {' · '}
                  {formatWhen(b.scheduled_start)} · {formatDuration(b.duration_minutes)}
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge tone={mine ? 'teal' : 'neutral'}>{mine ? 'You are providing' : 'You booked'}</Badge>
          <Badge tone={STATUS_TONE[b.status]}>{STATUS_LABEL[b.status]}</Badge>
        </div>
      </div>

      {/* Location is only shown once confirmed — §65: the exact meeting place
          reaches the other person after a booking exists, not before. */}
      {b.location_text && b.status !== 'requested' && (
        <p className="mt-3 text-sm">
          <span className="text-[var(--ink-2)]">Where: </span>
          {b.location_text}
        </p>
      )}

      {b.meeting_note && (
        <p className="mt-2 text-sm text-[var(--ink-2)]">&ldquo;{b.meeting_note}&rdquo;</p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--line-soft)] pt-4">
        <span className="flex items-center gap-1.5 text-sm">
          <Coin className="text-[var(--marigold)]" />
          <span className="font-semibold tabular-nums">
            {(b.reserved_coins || b.price_coins).toLocaleString('en-IN')}
          </span>
          <span className="text-[var(--ink-2)]">
            {b.status === 'requested' ? 'when accepted' : 'held'}
          </span>
        </span>

        {confirming === 'cancel' ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--ink-2)]">
              {b.status === 'confirmed' && !mine
                ? 'Cancelling now may not refund in full.'
                : 'Are you sure?'}
            </span>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onCancel()}>
              Yes, cancel
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirming(null)}>
              Keep it
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {/*
              Only the companion can accept, only a request, and only one that
              has not run out of time. fn_booking_accept refuses an expired
              request, so showing the button was offering an action the server
              would always reject — which reads as a broken button rather than
              a missed deadline.
            */}
            {mine && b.status === 'requested' && !isExpiredRequest(b) && (
              <Button size="sm" disabled={busy} onClick={onAccept}>
                {busy ? 'Working…' : 'Accept'}
              </Button>
            )}

            {mine && isExpiredRequest(b) && (
              <span className="text-sm text-[var(--muted)]">
                This request ran out of time.
              </span>
            )}

            {b.status === 'confirmed' && (
              <Button size="sm" disabled={busy} onClick={onComplete}>
                Mark done
              </Button>
            )}

            {mine && b.status === 'confirmed' && (
              <Button size="sm" variant="secondary" disabled={busy} onClick={onNoShow}>
                No-show
              </Button>
            )}

            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirming('cancel')}>
              {mine && b.status === 'requested' ? 'Decline' : 'Cancel'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function PastRow({ booking: b }: { booking: Booking }) {
  const refunded = b.refunded_coins > 0;

  return (
    <Card as="li" className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <p className="font-medium">
          {b.category_name}
          <span className="ml-2 text-sm font-normal text-[var(--ink-2)]">
            {bookingTypeLabel(b.booking_type)}
          </span>
        </p>
        <p className="mt-0.5 text-xs text-[var(--muted)]">
          {formatWhen(b.scheduled_start ?? b.requested_at)}
          {b.status_reason && ` · ${b.status_reason}`}
        </p>
      </div>

      <div className="flex items-center gap-3">
        {refunded && (
          <span className="text-sm text-[var(--teal)] tabular-nums">
            {b.refunded_coins.toLocaleString('en-IN')} refunded
          </span>
        )}
        <Badge tone={STATUS_TONE[b.status]}>{STATUS_LABEL[b.status]}</Badge>
      </div>
    </Card>
  );
}
